import React, { useMemo, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAriOriginalRequest } from '~/hooks/apis/ari/useAriRoomRateLookup';

import { AriRoomRateActivityRow } from '~/services/ReportingService';

import { formatBytes, formatPayload } from './formatPayload';

interface Props {
  row: AriRoomRateActivityRow | null;
  onClose: () => void;
}

export default function AriPayloadDialog({ row, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const { data, isFetching, error } = useAriOriginalRequest(
    row ? { ariLogId: row.ari_log_id, sourceService: row.source_service, createdAt: row.created_at } : null,
  );

  const payload = data?.data.original_request;
  const formatted = useMemo(() => (payload ? formatPayload(payload) : ''), [payload]);

  async function handleCopy() {
    if (!payload) {
      return;
    }
    await navigator.clipboard.writeText(payload);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Raw payload
        </Typography>
        {row && (
          <Typography variant="body2" color="text.secondary">
            {row.message_name} &middot; {row.source_service} &middot; {row.created_at}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {row && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
            ari_log_id <code>{row.ari_log_id}</code>
            {payload ? ` · ${formatBytes(new Blob([payload]).size)}` : ''}
          </Typography>
        )}

        {isFetching && (
          <Stack direction="row" spacing={1.5} alignItems="center" py={4}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Fetching payload from ari_logs&hellip;
            </Typography>
          </Stack>
        )}

        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              A 404 here usually means the created_at no longer falls inside the fixed &plusmn;1hr window the API binds
              around it — re-run the search to get a fresh row.
            </Typography>
          </Alert>
        )}

        {formatted && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              maxHeight: '60vh',
              overflow: 'auto',
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              lineHeight: 1.55,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
          >
            {formatted}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={handleCopy} disabled={!payload}>
          {copied ? 'Copied' : 'Copy raw'}
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
